import { CATEGORIES, isPending, titleFor } from './categories.js'
import styles from './CategoryRail.module.css'

/**
 * THE CATEGORY RAIL — the Command Center's top-level space switcher.
 *
 * Rendered straight off the `CATEGORIES` registry: every space is a real,
 * reachable tab, including the ones whose data line isn't sourced yet. Those
 * carry a visible "pending" mark instead of a count, so a rep can see the space
 * exists and that it is honestly empty — never a fake number, never a dead tab.
 */
export default function CategoryRail({ value, onChange, counts = {} }) {
  const selected = CATEGORIES.find(c => c.key === value) || CATEGORIES[0]
  return <div className={styles.picker}>
    <label htmlFor="cc-space">Opportunity space</label>
    <select id="cc-space" className="input" value={selected.key} onChange={e => onChange(e.target.value)}>
      {CATEGORIES.map(c => <option key={c.key} value={c.key}>{titleFor(c)}{isPending(c) ? ' · data pending' : counts[c.key] != null ? ` · ${counts[c.key].toLocaleString()}` : ''}</option>)}
    </select>
    <span>{CATEGORIES.length} spaces · all available here</span>
  </div>
}

/**
 * The honest empty state for a space with no records behind it — either its
 * line isn't sourced yet (`pending-data`) or the live line came back with
 * nothing. It states which of the two it is; it never renders a zeroed board
 * that looks like a working view of an empty world.
 */
export function SpaceEmpty({ category, reason = 'pending', error = '', onRetry }) {
  const pending = reason === 'pending'
  // Championships has no pursuit line — name the source it actually reads.
  const sourceName = category.source === 'championships'
    ? 'HubSpot championship object'
    : `${category.line} pursuit line`
  return (
    <div className={`card ${styles.empty}`} style={{ '--accent': category.accent }}>
      <div className={styles.emptyMark} />
      <div className={styles.emptyBody}>
        <div className={styles.emptyEyebrow}>{titleFor(category)}</div>
        <h3 className={styles.emptyTitle}>
          {pending ? 'Being fed in — no records yet' : `No ${category.nounPlural} came back`}
        </h3>
        <p className={styles.emptyLead}>
          {pending ? (
            <>
              This space is real and wired to the <code>{category.line}</code> pursuit line, but that line isn’t
              sourcing records yet. Nothing is hidden and nothing is estimated — the day the line returns
              {' '}{category.nounPlural}, every view here fills in with no code change.
            </>
          ) : (
            <>
              The <code>{sourceName}</code> answered, but with no records for this space
              {error ? ` (${error})` : ''}. That is reported as-is rather than backfilled with a placeholder.
            </>
          )}
        </p>
        <p className={styles.emptyNote}>{category.blurb} Read-only — this space issues no writes.</p>
        {!pending && onRetry && (
          <button className="btn btn-primary" onClick={onRetry}>Retry</button>
        )}
      </div>
    </div>
  )
}
